import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { clickGradebookSearch } from '../../../../actions/analyticsEvents';
import {
  getGradebook,
  getGradebookInstructor,
  setGradebookSearchPhrase,
  updateAllAverages,
  updateSearchType,
  updateSearchTypeMenu,
} from '../../../../actions/gradebook';
import { sendAnalyticsEvent } from '../../../../actions/sendAnalytics';
import {
  availableSearchTypesSelector,
  isGradebookV2ActiveSelector,
  searchPhraseSelector,
  searchTypeMenuAnchorElSelector,
  searchTypeMenuOpenSelector,
  searchTypeSelector,
} from '../../../../selectors/search-gradebook';
import { getConfigObject } from '../../../../utils/config';
import { SEARCH_TYPES } from '../../../../utils/constants';
import MLButton from '../../../MLButton';
import MLIcon from '../../../MLIcon';
import MLTextField from '../../../MLTextField/MLTextField';
import { CLOSE_SIDE_PANEL } from '../../../SidePanel/store/actions';
import withSidePanel from '../../../SidePanel/withSidePanel';
import SearchOptionsMenu from '../SearchOptionsMenu/SearchOptionsMenu';
import s from './SearchGradebook.module.css';

function SearchGradebook({
  // Props
  courseId,
  disableEnforceFocus,
  // State
  searchPhrase,
  searchType,
  searchTypeMenuOpen,
  searchTypeMenuAnchorEl,
  availableSearchTypes,
  isGradebookV2Active,
  // Redux
  getGradebook,
  getGradebookInstructor,
  setGradebookSearchPhrase,
  updateSearchType,
  updateSearchTypeMenu,
  updateAllAverages,
  sendAnalyticsEvent,
  // Side Panel
  sidePanelState,
  sidePanelDispatch,
}) {
  const { FF_SIDE_PANEL_ACTIVE: isSidePanelActive = false } = getConfigObject();

  const focusRef = useRef();
  const hasSearched = useRef(false);

  const [currentSearchType, setCurrentSearchType] = useState(searchType);

  const [currentSearchPhrase, setCurrentSearchPhrase] = useState(searchPhrase);
  const [debouncedSearchPhrase, setDebouncedSearchPhrase] =
    useState(searchPhrase);

  const updateDebouncedSearchPhrase = useMemo(
    () => _debounce(setDebouncedSearchPhrase, 500),
    [],
  );

  useEffect(() => {
    updateSearchType(currentSearchType);

    // Old behavior is handled by the option itself
    if (searchTypeMenuOpen && isSidePanelActive) {
      updateSearchTypeMenu({ open: false });
    }
  }, [
    currentSearchType,
    isSidePanelActive,
    searchTypeMenuOpen,
    updateSearchType,
    updateSearchTypeMenu,
  ]);

  useEffect(() => {
    if (!hasSearched.current) {
      // Do not search if no phrase provided or no type selected
      if (!debouncedSearchPhrase || !currentSearchType) {
        return;
      }
      hasSearched.current = true;
    }

    const options = {
      search: debouncedSearchPhrase,
      currentSearchType,
    };

    if (currentSearchType === SEARCH_TYPES.STUDENT) {
      options.page = 1;
    }

    setGradebookSearchPhrase(debouncedSearchPhrase);
    if (isGradebookV2Active) {
      getGradebookInstructor(courseId, options);
    } else {
      getGradebook(courseId, options);
    }
    updateAllAverages();
  }, [
    debouncedSearchPhrase,
    currentSearchType,
    setGradebookSearchPhrase,
    getGradebook,
    getGradebookInstructor,
    courseId,
    updateAllAverages,
    isGradebookV2Active,
  ]);

  const searchTypeLabel = (value) => {
    const { STUDENT, ASSIGNMENTS } = SEARCH_TYPES;
    switch (value) {
      case STUDENT:
        return 'Student';
      case ASSIGNMENTS:
        return 'Assignments';
      default:
        return 'Select';
    }
  };

  const searchTypePlaceholderIcon = (value) => {
    const { STUDENT, ASSIGNMENTS } = SEARCH_TYPES;
    return {
      [STUDENT]: {
        placeholder: 'Search Student',
        icon: 'person_group',
        label: 'Student',
      },
      [ASSIGNMENTS]: {
        placeholder: 'Search Assignment',
        icon: 'blank_document',
        label: 'Assignment',
      },
    }[value];
  };

  const handleSearchTextFieldFocus = (_) => {
    if (sidePanelState.open) {
      sidePanelDispatch({ type: CLOSE_SIDE_PANEL });
    }
    sendAnalyticsEvent(
      clickGradebookSearch(courseId, searchTypeLabel(searchType)),
    );
  };

  const handleSearchTextFieldChange = (event) => {
    // Update the current phrase to update search UI immediately (eg icon)
    setCurrentSearchPhrase(event.target.value);
    // Update the debounced phrase to trigger the actual search after debounce
    updateDebouncedSearchPhrase(event.target.value);
  };

  const handleSearchPhraseReset = () => {
    // Clear all search phrases immediately, ignoring debounce
    setCurrentSearchPhrase('');
    setDebouncedSearchPhrase('');
    // Refocus on the now cleared text area
    focusRef.current?.focus();
  };

  const renderDefaultSearchTypeDropdown = () => {
    return (
      <>
        <MLButton
          btnType="secondary"
          className={s.searchTypeSelector}
          ariaExpanded={searchTypeMenuOpen}
          testId="search-button"
          onClick={(event) =>
            updateSearchTypeMenu({
              open: true,
              anchorEl: event.currentTarget,
            })
          }
        >
          <span>{searchTypeLabel(searchType)}</span>
          <MLIcon
            className={`${s.headerBtnIcon} ${s.tinyChevronIcon}`}
            type="caret_down"
          />
        </MLButton>
        <Popover
          open={searchTypeMenuOpen}
          anchorEl={searchTypeMenuAnchorEl}
          classes={{ paper: `${s.popoverPaper}` }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableEnforceFocus={disableEnforceFocus}
          onClose={() => updateSearchTypeMenu({ open: false })}
        >
          <MenuList>
            {availableSearchTypes.map((searchOption) => (
              <MenuItem
                key={searchOption.type}
                onClick={() => {
                  setCurrentSearchType(searchOption.type);
                  updateSearchTypeMenu({ open: false });
                }}
                data-test-id={`${searchOption.label}-search`}
              >
                {searchOption.label}
              </MenuItem>
            ))}
          </MenuList>
        </Popover>
      </>
    );
  };

  const renderSidePanelEnabledSearchTypeDropdown = () => {
    const { icon, label } = searchTypePlaceholderIcon(searchType);

    return (
      <SearchOptionsMenu
        activeIcon={icon}
        searchType={searchType}
        buttonLabel={label}
        availableSearchTypes={availableSearchTypes}
        disableEnforceFocus={disableEnforceFocus}
        handleSearchTypeChange={(updatedSearchType) =>
          setCurrentSearchType(updatedSearchType)
        }
      />
    );
  };

  return (
    <div
      className={
        isSidePanelActive ? s.searchBoxWrapper : s.searchFoATxtBoxWrapper
      }
    >
      {isSidePanelActive
        ? renderSidePanelEnabledSearchTypeDropdown()
        : renderDefaultSearchTypeDropdown()}
      <MLTextField
        inputRef={focusRef}
        type="text"
        name="filter"
        containerClassName={s.searchFoATxtBoxContainer}
        className={s.searchFoATxtBox}
        placeholder={
          (isSidePanelActive &&
            searchTypePlaceholderIcon(searchType).placeholder) ||
          'Search'
        }
        value={currentSearchPhrase}
        onFocus={handleSearchTextFieldFocus}
        onChange={handleSearchTextFieldChange}
        iconOnClick={currentSearchPhrase ? handleSearchPhraseReset : null}
        iconButtonAriaLabel={currentSearchPhrase ? 'Clear search' : null}
        iconType={currentSearchPhrase ? 'x' : 'search'}
        iconClassName={currentSearchPhrase ? s.clearIcon : s.searchIcon}
        testId={'user_search_input'}
      />
    </div>
  );
}

SearchGradebook.propTypes = {
  // Props
  courseId: PropTypes.string.isRequired,
  disableEnforceFocus: PropTypes.bool.isRequired,
  // State
  searchPhrase: PropTypes.string.isRequired,
  searchType: PropTypes.string.isRequired,
  searchTypeMenuOpen: PropTypes.bool.isRequired,
  searchTypeMenuAnchorEl: PropTypes.object,
  availableSearchTypes: PropTypes.array.isRequired,
  isGradebookV2Active: PropTypes.bool.isRequired,
  // Redux
  getGradebook: PropTypes.func.isRequired,
  getGradebookInstructor: PropTypes.func.isRequired,
  setGradebookSearchPhrase: PropTypes.func.isRequired,
  updateSearchType: PropTypes.func.isRequired,
  updateSearchTypeMenu: PropTypes.func.isRequired,
  updateAllAverages: PropTypes.func.isRequired,
  sendAnalyticsEvent: PropTypes.func.isRequired,
  // Side Panel
  sidePanelState: PropTypes.object.isRequired,
  sidePanelDispatch: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  getGradebook: (courseId, options) =>
    dispatch(getGradebook(courseId, options)),
  getGradebookInstructor: (courseId, options) =>
    dispatch(getGradebookInstructor(courseId, options)),
  setGradebookSearchPhrase: (searchPhrase) =>
    dispatch(setGradebookSearchPhrase(searchPhrase)),
  updateSearchType: (searchType) => dispatch(updateSearchType(searchType)),
  updateSearchTypeMenu: ({ open, anchorEl }) =>
    dispatch(updateSearchTypeMenu({ open, anchorEl })),
  updateAllAverages: () => dispatch(updateAllAverages()),
  sendAnalyticsEvent: (event) => dispatch(sendAnalyticsEvent(event)),
});

const mapStateToProps = (state) => ({
  searchPhrase: searchPhraseSelector(state),
  searchType: searchTypeSelector(state),
  searchTypeMenuOpen: searchTypeMenuOpenSelector(state),
  searchTypeMenuAnchorEl: searchTypeMenuAnchorElSelector(state),
  availableSearchTypes: availableSearchTypesSelector(state),
  isGradebookV2Active: isGradebookV2ActiveSelector(state),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withSidePanel(SearchGradebook));
